import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FormCaption,
  InformSpan,
  FormSubmit,
  Input,
  ArrowLink
} from '../../common/components';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { EmailErrors, AUTH_PATTERN } from './validation.pattern';
import { authService } from '../service/auth.service';
import { APP_KEYS } from '../../common/consts';
import { useThrottle } from '../../hooks/useThrottle';
import { EmailInputIcon } from '../../common/components/icons/email-input-icon.component';

type Inputs = {
  email: string;
};

export const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<Inputs>();

  const { mutate: getRecoveryCode, isPending: getRecoveryCodePending } = useMutation(
    {
      mutationKey: [APP_KEYS.QUERY_KEYS.RECOVERY_CODE],
      mutationFn: (data: Inputs) => authService.forgotPassword(data),
      onSuccess: (email: string) => {
        navigate(ROUTER_LINKS.PASSWORD_RECOVERY, { state: { email } });
      },
      onError: (err: any) => {
        if (err.response.data.message.includes('User not found')) {
          setError('email', {
            message: t('auth.accountNotFound'),
            type: 'manual'
          });
        }
      }
    }
  );
  const throttledGetRecoveryCode = useThrottle(getRecoveryCode);

  const onSubmit: SubmitHandler<Inputs> = (data: Inputs) => {
    throttledGetRecoveryCode(data);
  };

  return (
    <>
      <FormCaption>{t('auth.forgotTitle')}</FormCaption>
      <InformSpan>{t('auth.forgotSubtitle')}</InformSpan>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <Input
          errors={errors}
          name="email"
          label={t('auth.email')}
          placeholder={t('auth.enterHere')}
          type="email"
          icon={<EmailInputIcon />}
          register={register.bind(null, 'email', {
            required: { message: EmailErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.email,
              message: EmailErrors.pattern
            }
          })}
        />

        <FormSubmit
          className="md:mt-[2px] mb-[24px] md:mb-[24px]"
          isLoading={getRecoveryCodePending}
          value={t('auth.sendEmail')}
        />

        <ArrowLink linkText={t('auth.backToLogin')} to={ROUTER_LINKS.SIGNIN} />
      </form>
    </>
  );
};
